import { useState, useEffect } from "react";
import {
  Truck,
  Mail,
  Sparkles,
  Cpu,
  Phone,
  Users,
  Megaphone,
  BarChart3,
  LifeBuoy,
  Menu as MenuIcon,
  X,
} from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import PageLayout from "examples/LayoutContainers/PageLayout";
import { cn } from "utils/cnUtils";
import "./index.css";
import { useTranslation } from "react-i18next";
import { Button } from "components/Button";
import { useNavigate } from "react-router-dom";
import LanguageSwitcher from "components/LanguageSwitcher.js";
import SupportModal from "./components/SupportModal";
import expertHero from "assets/images/home/expert_repair_hero.png";
import titanLogo from "assets/images/logo.svg";

const HomePage = () => {
  // eslint-disable-next-line
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [scrolled, setScrolled] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen text-foreground selection:bg-primary/30 overflow-x-hidden">
      <PageLayout>
        {/* Navigation */}
        <nav
          className={cn(
            "fixed top-0 left-0 right-0 z-50 transition-all duration-500 px-4 sm:px-6 py-4",
            scrolled
              ? "py-3 navbar-scrolled mt-4 mx-4 rounded-2xl"
              : "bg-transparent text-white",
          )}
        >
          <div className="container mx-auto flex items-center justify-between">
            <div
              className="flex items-center gap-1 cursor-pointer group"
              onClick={() => {
                navigate("/");
                setMobileMenuOpen(false);
              }}
            >
              <div className="relative">
                <img
                  src={titanLogo}
                  alt="Titan LCD"
                  className="h-10 w-10 sm:h-12 sm:w-12 object-contain"
                />
              </div>
              <span className="text-xl sm:text-2xl font-bold tracking-tight text-gradient">
                Titan LCD
              </span>
            </div>

            <div className="flex items-center gap-4 sm:gap-6">
              <div className="hidden md:flex items-center gap-8">
                {["features", "contact"].map((item) => (
                  <a
                    key={item}
                    href={`#${item}`}
                    className={cn(
                      "text-sm font-medium hover:text-primary transition-colors uppercase tracking-widest",
                      scrolled ? "text-foreground/70" : "text-white/70",
                    )}
                  >
                    {t(item)}
                  </a>
                ))}
              </div>
              <LanguageSwitcher />

              <div className="hidden sm:flex">
                <Button
                  size="sm"
                  className="rounded-full px-6 bg-primary"
                  onClick={() => navigate("/authentication/connexion")}
                >
                  {t("Dashboard")}
                </Button>
              </div>

              {/* Mobile Menu Toggle */}
              <button
                className="flex md:hidden text-white"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? (
                  <X className={cn("h-8 w-8", scrolled && "text-foreground")} />
                ) : (
                  <MenuIcon className={cn("h-8 w-8", scrolled && "text-foreground")} />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu Overlay */}
          <div
            className={cn(
              "md:hidden fixed inset-0 z-[60] transition-all duration-500",
              mobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
            )}
          >
            {/* Backdrop Blur */}
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-xl"
              onClick={() => setMobileMenuOpen(false)}
            />

            {/* Menu Content */}
            <div
              className={cn(
                "absolute top-0 right-0 bottom-0 w-[80%] max-w-sm bg-zinc-900 shadow-2xl transition-transform duration-500 ease-out transform border-l border-white/5",
                mobileMenuOpen ? "translate-x-0" : "translate-x-full"
              )}
            >
              <div className="flex flex-col h-full p-8">
                {/* Header in Menu */}
                <div className="flex items-center justify-between mb-12">
                  <div className="flex items-center gap-2">
                    <img src={titanLogo} alt="Logo" className="h-8 w-8" />
                    <span className="font-bold text-xl text-gradient">Titan LCD</span>
                  </div>
                  <button
                    onClick={() => setMobileMenuOpen(false)}
                    className="p-2 rounded-full bg-white/5 text-white hover:bg-white/10 transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                {/* Navigation Links */}
                <div className="flex flex-col gap-6">
                  {["features", "contact"].map((item, index) => (
                    <a
                      key={item}
                      href={`#${item}`}
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "text-xl font-bold uppercase tracking-widest text-white hover:text-primary transition-all duration-300 transform",
                        mobileMenuOpen ? "translate-x-0 opacity-100" : "translate-x-10 opacity-0"
                      )}
                      style={{ transitionDelay: `${index * 100}ms` }}
                    >
                      {t(item)}
                    </a>
                  ))}
                </div>

                <div className="mt-auto pt-8">
                  <div className="h-px w-full bg-white/5 mb-8" />

                  <p className="text-zinc-500 text-[10px] font-bold mb-6 uppercase tracking-[0.3em]">
                    {t("Gestion Professionnelle")}
                  </p>

                  <Button
                    size="lg"
                    className="w-full rounded-2xl bg-primary py-6 text-lg font-bold shadow-glow-primary hover:scale-[1.02] transition-transform"
                    onClick={() => {
                      navigate("/authentication/connexion");
                      setMobileMenuOpen(false);
                    }}
                  >
                    {t("Dashboard")}
                  </Button>

                  <div className="mt-8 flex justify-center">
                    <LanguageSwitcher />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section - DARK START */}
        <section className="relative min-h-screen flex items-center pt-24 pb-12 overflow-hidden section-dark dark-mesh-gradient">
          <div className="absolute inset-0 tech-grid opacity-20"></div>
          <div className="scanline"></div>
          {/* Ambient Glow */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-6xl aspect-square bg-primary/5 rounded-full blur-[160px] -z-10 animate-pulse-slow"></div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="grid lg:grid-cols-2 items-center gap-12 lg:gap-20">
              {/* Left Column: Content */}
              <div className="space-y-8 sm:space-y-12 text-left">
                <div className="animate-fade-in flex justify-start">
                  <span className="inline-flex items-center px-5 py-2 rounded-full glass border border-white/10 text-primary text-sm font-semibold tracking-wide">
                    <Sparkles className="h-4 w-4 mr-2 text-accent" />
                    {t("Expert Phone Repair Solutions")}
                  </span>
                </div>

                <h1 className="text-5xl sm:text-7xl lg:text-7xl xl:text-8xl font-black leading-[1.05] animate-slide-up tracking-tighter text-white">
                  <span className="block">{t("Elevate Your")}</span>
                  <span className="text-gradient drop-shadow-xl">
                    {t("Repair Workshop")}
                  </span>
                </h1>

                <p className="text-xl sm:text-2xl text-zinc-400 max-w-2xl animate-fade-in leading-relaxed font-light">
                  {t("landingPageText")}
                </p>

                <div className="flex items-center gap-8 pt-6 animate-slide-up">
                  <div className="relative group">
                    <div className="absolute -inset-2 bg-primary/20 rounded-[1.5rem] blur opacity-0 group-hover:opacity-100 transition duration-500"></div>
                    <div className="relative glass p-3.5 rounded-2xl border-white/20 shadow-2xl overflow-hidden bg-white/5">
                      <QRCodeCanvas
                        value="https://play.google.com/store/apps/details?id=com.livrili.livrilireparation"
                        size={72}
                        level={"H"}
                        includeMargin={false}
                        imageSettings={{
                          src: titanLogo,
                          x: undefined,
                          y: undefined,
                          height: 16,
                          width: 16,
                          excavate: true,
                        }}
                        className="rounded-lg relative z-10 mix-blend-screen"
                      />
                    </div>
                  </div>

                  <a
                    href="https://play.google.com/store/apps/details?id=com.livrili.livrilireparation"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative transition-all duration-300 hover:scale-[1.02]"
                  >
                    <div className="absolute -inset-4 bg-primary/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition duration-500"></div>
                    <img
                      src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                      alt="Get it on Google Play"
                      className="h-[84px] w-auto object-contain relative z-10"
                    />
                  </a>
                </div>
              </div>

              {/* Right Column: Image */}
              <div className="relative animate-float lg:flex lg:justify-end">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl aspect-square bg-primary/20 rounded-full blur-[100px] -z-10 animate-pulse-slow"></div>
                <div className="relative group max-w-lg lg:max-w-xl">
                  <img
                    src={expertHero}
                    alt="Expert Phone Repair"
                    className="rounded-[3rem] w-full h-auto object-cover shadow-2xl ring-1 ring-white/10 perspective rotate-y-6"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid - REVERTED TO ORIGINAL AS PER USER REQUEST */}
        <section
          id="features"
          className="py-24 sm:py-32 relative section-light"
        >
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-black mb-6 tracking-tighter text-foreground">
                {t("Our Features")}
              </h2>
              <p className="text-lg text-foreground/60 max-w-2xl mx-auto font-medium leading-relaxed">
                {t("Our Features Description")}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* 1. Smart Inventory */}
              <div className="glass-card rounded-[2.5rem] p-10 hover:shadow-2xl transition-all relative overflow-hidden group">
                <div className="absolute top-8 right-10 text-[10px] font-bold uppercase tracking-[0.2em] text-foreground/30 font-mono">
                  {t("Storage")}
                </div>
                <div className="h-16 w-16 bg-primary/10 rounded-2xl flex items-center justify-center text-primary mb-8 shadow-sm border border-primary/10 icon-box-pro">
                  <Cpu className="h-8 w-8" />
                </div>
                <h3 className="text-2xl font-black mb-4 text-foreground tracking-tight">
                  {t("Smart Inventory")}
                </h3>
                <p className="text-base text-foreground/50 leading-relaxed font-medium mb-8">
                  {t("Smart Inventory Description")}
                </p>
                <div className="flex items-center gap-2">
                  <div className="h-px w-8 bg-primary/20"></div>
                  <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-primary/60 font-mono">
                    {t("LIVE MANAGEMENT")}
                  </span>
                </div>
              </div>

              {/* 2. Express Logistics */}
              <div className="glass-card rounded-[2.5rem] p-10 hover:shadow-2xl transition-all relative overflow-hidden group">
                <div className="absolute top-8 right-10 text-[10px] font-bold uppercase tracking-[0.2em] text-foreground/30 font-mono">
                  {t("Logistics")}
                </div>
                <div className="h-16 w-16 bg-primary/10 rounded-2xl flex items-center justify-center text-primary mb-8 shadow-sm border border-primary/10 icon-box-pro">
                  <Truck className="h-8 w-8" />
                </div>
                <h3 className="text-2xl font-black mb-4 text-foreground tracking-tight">
                  {t("Express Logistics")}
                </h3>
                <p className="text-base text-foreground/50 leading-relaxed font-medium mb-8">
                  {t("Express Logistics Description")}
                </p>
                <div className="flex items-center gap-2">
                  <div className="h-px w-8 bg-primary/20"></div>
                  <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-primary/60 font-mono">
                    {t("GLOBAL ROUTING")}
                  </span>
                </div>
              </div>

              {/* 3. Professional CRM */}
              <div className="glass-card rounded-[2.5rem] p-10 hover:shadow-2xl transition-all relative overflow-hidden group">
                <div className="absolute top-8 right-10 text-[10px] font-bold uppercase tracking-[0.2em] text-foreground/30 font-mono">
                  {t("CRM")}
                </div>
                <div className="h-16 w-16 bg-primary/10 rounded-2xl flex items-center justify-center text-primary mb-8 shadow-sm border border-primary/10 icon-box-pro">
                  <Users className="h-8 w-8" />
                </div>
                <h3 className="text-2xl font-black mb-4 text-foreground tracking-tight">
                  {t("Professional CRM")}
                </h3>
                <p className="text-base text-foreground/50 leading-relaxed font-medium mb-8">
                  {t("Professional CRM Description")}
                </p>
                <div className="flex items-center gap-2">
                  <div className="h-px w-8 bg-primary/20"></div>
                  <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-primary/60 font-mono">
                    {t("WORKSHOP SHIELD")}
                  </span>
                </div>
              </div>

              {/* 4. Growth Engine */}
              <div className="glass-card rounded-[2.5rem] p-10 hover:shadow-2xl transition-all relative overflow-hidden group">
                <div className="absolute top-8 right-10 text-[10px] font-bold uppercase tracking-[0.2em] text-foreground/30 font-mono">
                  {t("Growth")}
                </div>
                <div className="h-16 w-16 bg-primary/10 rounded-2xl flex items-center justify-center text-primary mb-8 shadow-sm border border-primary/10 icon-box-pro">
                  <Megaphone className="h-8 w-8" />
                </div>
                <h3 className="text-2xl font-black mb-4 text-foreground tracking-tight">
                  {t("Growth Engine")}
                </h3>
                <p className="text-base text-foreground/50 leading-relaxed font-medium mb-8">
                  {t("Growth Engine Description")}
                </p>
                <div className="flex items-center gap-2">
                  <div className="h-px w-8 bg-primary/20"></div>
                  <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-primary/60 font-mono">
                    {t("ACTIVE CAMPAIGNS")}
                  </span>
                </div>
              </div>

              {/* 5. Business Analytics */}
              <div className="glass-card rounded-[2.5rem] p-10 hover:shadow-2xl transition-all relative overflow-hidden group">
                <div className="absolute top-8 right-10 text-[10px] font-bold uppercase tracking-[0.2em] text-foreground/30 font-mono">
                  {t("Data")}
                </div>
                <div className="h-16 w-16 bg-primary/10 rounded-2xl flex items-center justify-center text-primary mb-8 shadow-sm border border-primary/10 icon-box-pro">
                  <BarChart3 className="h-8 w-8" />
                </div>
                <h3 className="text-2xl font-black mb-4 text-foreground tracking-tight">
                  {t("Business Analytics")}
                </h3>
                <p className="text-base text-foreground/50 leading-relaxed font-medium mb-8">
                  {t("Business Analytics Description")}
                </p>
                <div className="flex items-center gap-2">
                  <div className="h-px w-8 bg-primary/20"></div>
                  <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-primary/60 font-mono">
                    {t("LIVE INSIGHTS")}
                  </span>
                </div>
              </div>

              {/* 6. Tech Support */}
              <div className="glass-card rounded-[2.5rem] p-10 hover:shadow-2xl transition-all relative overflow-hidden group">
                <div className="absolute top-8 right-10 text-[10px] font-bold uppercase tracking-[0.2em] text-foreground/30 font-mono">
                  {t("Support")}
                </div>
                <div className="h-16 w-16 bg-primary/10 rounded-2xl flex items-center justify-center text-primary mb-8 shadow-sm border border-primary/10 icon-box-pro">
                  <LifeBuoy className="h-8 w-8" />
                </div>
                <h3 className="text-2xl font-black mb-4 text-foreground tracking-tight">
                  {t("Tech Support")}
                </h3>
                <p className="text-base text-foreground/50 leading-relaxed font-medium mb-8">
                  {t("Tech Support Description")}
                </p>
                <div className="flex items-center gap-2">
                  <div className="h-px w-8 bg-primary/20"></div>
                  <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-primary/60 font-mono">
                    {t("24/7 EXPERT")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA & Contact Section - DARK SECTION */}
        <section
          id="contact"
          className="py-24 sm:py-48 relative overflow-hidden section-dark dark-mesh-gradient"
        >
          <div className="absolute inset-0 tech-grid opacity-10"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="glass rounded-[4rem] p-12 sm:p-24 text-center border border-white/5 shadow-2xl">
              <h2 className="text-4xl sm:text-7xl font-black mb-8 tracking-tighter max-w-4xl mx-auto text-white">
                {t("Ready to Upgrade Your Workshop?")}
              </h2>
              <p className="text-xl text-zinc-400 mb-12 max-w-2xl mx-auto font-light">
                {t(
                  "Join thousands of elite technicians using Titan LCD's professional repair systems.",
                )}
              </p>

              <div className="flex flex-col items-center gap-8">
                <Button
                  size="lg"
                  onClick={() => setIsSupportModalOpen(true)}
                  className="rounded-full px-12 py-5 text-xl bg-primary hover:scale-105 transition-transform shadow-glow-primary"
                >
                  {t("Contact Expert Support")}
                </Button>

                <div className="flex flex-wrap justify-center gap-8 mt-4 text-zinc-500 font-medium">
                  <div className="flex items-center gap-3 glass px-6 py-3 rounded-2xl border border-white/5">
                    <Mail className="h-5 w-5 text-primary" />
                    <a
                      href="mailto:rejeb.chaalia@gmail.com"
                      className="hover:text-primary transition-colors"
                    >
                      rejeb.chaalia@gmail.com
                    </a>
                  </div>
                  <div className="flex items-center gap-3 glass px-6 py-3 rounded-2xl border border-white/5">
                    <Phone className="h-5 w-5 text-primary" />
                    <a
                      href="tel:++21652187320"
                      className="hover:text-primary transition-colors"
                    >
                      +216 52 187 320
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 border-t border-black/5 bg-white/40">
          <div className="container mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-8 text-foreground">
            <div className="flex items-center gap-3">
              <img
                src={titanLogo}
                alt="Titan LCD"
                className="h-10 w-10 object-contain"
              />
              <span className="font-bold tracking-tight">Titan LCD</span>
            </div>
            <p className="text-sm text-foreground/40 font-medium text-center">
              © {new Date().getFullYear()} {t("Titan LCD by Norma team")}.{" "}
              {t("All rights reserved")}.
            </p>
            <div className="flex gap-8 text-xs font-bold uppercase tracking-widest text-foreground/40">
              <a href="#!" className="hover:text-primary transition-colors">
                {t("Privacy")}
              </a>
              <a href="#!" className="hover:text-primary transition-colors">
                {t("Terms")}
              </a>
            </div>
          </div>
        </footer>

        <SupportModal
          isOpen={isSupportModalOpen}
          onClose={() => setIsSupportModalOpen(false)}
        />
      </PageLayout>
    </div>
  );
};

export default HomePage;
